import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as NavBackIcon } from '../../Icons/nav-back.svg';
import { ReactComponent as NavForwardIcon } from '../../Icons/nav-forward.svg';
import { ReactComponent as SearchIcon } from '../../Icons/search.svg';
import IconRenderer from '../ui/IconRenderer';
import { formatLanguageDisplay, normalizeRoadmapLanguageKey } from '../../utils/roadmapDisplay';
import { ROADMAP_LEARN_MODES } from './roadmapLearnHub';
import './RoadmapLearnSidebar.css';

/** Language rows per page when the catalogue exceeds {@link LANGUAGE_RAIL_SINGLE_PAGE_MAX}. */
const LANGUAGE_RAIL_ITEMS_PER_PAGE = 8;
/** Show every language on one page while count stays at or below this. */
const LANGUAGE_RAIL_SINGLE_PAGE_MAX = 8;
/** Role rows per page when the catalog exceeds {@link ROLE_RAIL_SINGLE_PAGE_MAX}. */
const ROLE_RAIL_ITEMS_PER_PAGE = 6;
/** Show every role on one page (no pager) while count stays at or below this — fits current 8 launch roles. */
const ROLE_RAIL_SINGLE_PAGE_MAX = 8;

function languageRailItemsPerPage(languageCount) {
  if (languageCount <= 0) return 1;
  if (languageCount <= LANGUAGE_RAIL_SINGLE_PAGE_MAX) return languageCount;
  return LANGUAGE_RAIL_ITEMS_PER_PAGE;
}

function roleRailItemsPerPage(roleCount) {
  if (roleCount <= 0) return 1;
  if (roleCount <= ROLE_RAIL_SINGLE_PAGE_MAX) return roleCount;
  return ROLE_RAIL_ITEMS_PER_PAGE;
}

function matchesLanguageQuery(lang, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const display = formatLanguageDisplay(lang).toLowerCase();
  const key = normalizeRoadmapLanguageKey(lang).toLowerCase();
  return display.includes(q) || key.includes(q) || String(lang).toLowerCase().includes(q);
}

function matchesRoleQuery(role, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return String(role.title || '').toLowerCase().includes(q);
}

/**
 * Left pane of the Learn split view: Language | Role toggle + path list.
 */
export default function RoadmapLearnSidebar({
  languages = [],
  roles = [],
  learnMode = ROADMAP_LEARN_MODES.language,
  onLearnModeChange,
  selectedLanguage = null,
  selectedRole = null,
  isLoadingTracks = false,
  lastVisitedLanguage = null,
  onSelectLanguage,
  onSelectRole
}) {
  const isLanguageMode = learnMode === ROADMAP_LEARN_MODES.language;
  const isRoleMode = learnMode === ROADMAP_LEARN_MODES.role;

  const [languagePage, setLanguagePage] = useState(0);
  const [rolePage, setRolePage] = useState(0);
  const [languageQuery, setLanguageQuery] = useState('');
  const [roleQuery, setRoleQuery] = useState('');

  const filteredLanguages = useMemo(
    () => languages.filter((lang) => matchesLanguageQuery(lang, languageQuery)),
    [languages, languageQuery]
  );
  const filteredRoles = useMemo(
    () => roles.filter((role) => matchesRoleQuery(role, roleQuery)),
    [roles, roleQuery]
  );

  const activeItems = isLanguageMode ? filteredLanguages : filteredRoles;
  const listPage = isLanguageMode ? languagePage : rolePage;
  const setListPage = isLanguageMode ? setLanguagePage : setRolePage;
  const searchQuery = isLanguageMode ? languageQuery : roleQuery;
  const setSearchQuery = isLanguageMode ? setLanguageQuery : setRoleQuery;

  const languageItemsPerPage = useMemo(
    () => languageRailItemsPerPage(filteredLanguages.length),
    [filteredLanguages.length]
  );
  const roleItemsPerPage = useMemo(
    () => roleRailItemsPerPage(filteredRoles.length),
    [filteredRoles.length]
  );
  const itemsPerPage = isLanguageMode ? languageItemsPerPage : roleItemsPerPage;

  const pageCount = Math.max(1, Math.ceil(activeItems.length / itemsPerPage));

  useEffect(() => {
    setLanguagePage(0);
  }, [languageQuery]);

  useEffect(() => {
    setRolePage(0);
  }, [roleQuery]);

  useEffect(() => {
    if (listPage > pageCount - 1) {
      setListPage(0);
    }
  }, [listPage, pageCount, setListPage]);

  useEffect(() => {
    if (!isLanguageMode || !selectedLanguage || filteredLanguages.length === 0) return;
    const selectedKey = normalizeRoadmapLanguageKey(selectedLanguage);
    const itemIdx = filteredLanguages.findIndex(
      (lang) => normalizeRoadmapLanguageKey(lang) === selectedKey
    );
    if (itemIdx < 0) return;
    const perPage = languageRailItemsPerPage(filteredLanguages.length);
    const targetPage = Math.floor(itemIdx / perPage);
    setLanguagePage((current) => (current === targetPage ? current : targetPage));
  }, [isLanguageMode, selectedLanguage, filteredLanguages]);

  useEffect(() => {
    if (!isRoleMode || !selectedRole?.id || filteredRoles.length === 0) return;
    const itemIdx = filteredRoles.findIndex((role) => role.id === selectedRole.id);
    if (itemIdx < 0) return;
    const perPage = roleRailItemsPerPage(filteredRoles.length);
    const targetPage = Math.floor(itemIdx / perPage);
    setRolePage((current) => (current === targetPage ? current : targetPage));
  }, [isRoleMode, selectedRole?.id, filteredRoles]);

  const visibleLanguages = useMemo(() => {
    const perPage = languageRailItemsPerPage(filteredLanguages.length);
    const start = languagePage * perPage;
    return filteredLanguages.slice(start, start + perPage);
  }, [filteredLanguages, languagePage]);

  const visibleRoles = useMemo(() => {
    const start = rolePage * roleItemsPerPage;
    return filteredRoles.slice(start, start + roleItemsPerPage);
  }, [filteredRoles, rolePage, roleItemsPerPage]);

  const listRangeStart = activeItems.length === 0 ? 0 : listPage * itemsPerPage + 1;
  const listRangeEnd = Math.min((listPage + 1) * itemsPerPage, activeItems.length);
  const canListPagePrev = listPage > 0;
  const canListPageNext = listPage < pageCount - 1;
  const catalogCount = isLanguageMode ? languages.length : roles.length;
  const showFooter = catalogCount > 0;
  const footerAriaLabel = isLanguageMode
    ? 'Language list controls'
    : 'Role list controls';
  const searchPlaceholder = 'Search…';
  const searchAriaLabel = isLanguageMode ? 'Search languages' : 'Search roles';
  const hasActiveSearch = searchQuery.trim().length > 0;
  const showFilteredEmpty =
    hasActiveSearch && activeItems.length === 0 && catalogCount > 0;

  const handleSearchChange = useCallback(
    (e) => {
      setSearchQuery(e.target.value);
    },
    [setSearchQuery]
  );

  const handleLanguageKeyDown = useCallback(
    (e, lang) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelectLanguage?.(lang);
      }
    },
    [onSelectLanguage]
  );

  const handleRoleKeyDown = useCallback(
    (e, role) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelectRole?.(role);
      }
    },
    [onSelectRole]
  );

  const handleModeKeyDown = useCallback(
    (e, mode) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onLearnModeChange?.(mode);
      }
    },
    [onLearnModeChange]
  );

  return (
    <aside className="roadmap-learn-sidebar" aria-label="Learn navigation">
      <div className="roadmap-learn-sidebar__body">
        <div className="roadmap-learn-sidebar__body-scroll">
          <header className="roadmap-learn-sidebar__chrome">
            <div className="roadmap-learn-sidebar__mode-section">
              <p
                className="roadmap-learn-sidebar__section-label roadmap-learn-sidebar__mode-label"
                id="roadmap-learn-sidebar-mode-label"
              >
                Browse by
              </p>
              <div
                className="roadmap-learn-sidebar__mode-toggle"
                role="tablist"
                aria-labelledby="roadmap-learn-sidebar-mode-label"
              >
                <button
                  type="button"
                  role="tab"
                  id="roadmap-learn-sidebar-mode-language"
                  aria-selected={isLanguageMode}
                  aria-controls="roadmap-learn-sidebar-panel-languages"
                  className={`roadmap-learn-sidebar__mode-toggle-btn ${isLanguageMode ? 'roadmap-learn-sidebar__mode-toggle-btn--active' : ''}`.trim()}
                  onClick={() => onLearnModeChange?.(ROADMAP_LEARN_MODES.language)}
                  onKeyDown={(e) => handleModeKeyDown(e, ROADMAP_LEARN_MODES.language)}
                >
                  Language
                </button>
                <button
                  type="button"
                  role="tab"
                  id="roadmap-learn-sidebar-mode-role"
                  aria-selected={isRoleMode}
                  aria-controls="roadmap-learn-sidebar-panel-roles"
                  className={`roadmap-learn-sidebar__mode-toggle-btn ${isRoleMode ? 'roadmap-learn-sidebar__mode-toggle-btn--active' : ''}`.trim()}
                  onClick={() => onLearnModeChange?.(ROADMAP_LEARN_MODES.role)}
                  onKeyDown={(e) => handleModeKeyDown(e, ROADMAP_LEARN_MODES.role)}
                >
                  Role
                </button>
              </div>
            </div>
          </header>

          <div className="roadmap-learn-sidebar__nav">
            {isLanguageMode ? (
              <section
                id="roadmap-learn-sidebar-panel-languages"
                className="roadmap-learn-sidebar__languages"
                aria-label="Languages"
                role="tabpanel"
                aria-labelledby="roadmap-learn-sidebar-mode-language"
              >
                {isLoadingTracks && languages.length === 0 ? (
                  <p className="roadmap-learn-sidebar__empty">Loading roadmaps…</p>
                ) : showFilteredEmpty ? (
                  <p className="roadmap-learn-sidebar__empty">No languages match your search.</p>
                ) : (
                  <ul className="roadmap-learn-sidebar__rail-list">
                    {visibleLanguages.map((lang) => {
                      const lk = normalizeRoadmapLanguageKey(lang);
                      const isSelected =
                        selectedLanguage && normalizeRoadmapLanguageKey(selectedLanguage) === lk;
                      const isLastVisited =
                        lastVisitedLanguage && normalizeRoadmapLanguageKey(lastVisitedLanguage) === lk;
                      return (
                        <li key={lang} className="roadmap-learn-sidebar__rail-item">
                          <button
                            type="button"
                            className={[
                              'roadmap-learn-sidebar__rail-btn',
                              'roadmap-learn-sidebar__rail-btn--language',
                              isSelected ? 'roadmap-learn-sidebar__rail-btn--selected' : '',
                              isLastVisited && !isSelected ? 'roadmap-learn-sidebar__rail-btn--last-visited' : ''
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            aria-current={isSelected ? 'true' : undefined}
                            onClick={() => onSelectLanguage?.(lang)}
                            onKeyDown={(e) => handleLanguageKeyDown(e, lang)}
                          >
                            <span className="roadmap-learn-sidebar__lang-icon" aria-hidden>
                              <IconRenderer
                                name={lang}
                                type="language"
                                size="medium"
                                className="roadmap-learn-sidebar__lang-icon-svg"
                              />
                            </span>
                            <span className="roadmap-learn-sidebar__rail-title">
                              {formatLanguageDisplay(lang)}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {languages.length === 0 && !isLoadingTracks ? (
                  <p className="roadmap-learn-sidebar__empty">No languages available.</p>
                ) : null}
              </section>
            ) : (
              <section
                id="roadmap-learn-sidebar-panel-roles"
                className="roadmap-learn-sidebar__roles"
                aria-label="Roles"
                role="tabpanel"
                aria-labelledby="roadmap-learn-sidebar-mode-role"
              >
                {roles.length > 0 ? (
                  showFilteredEmpty ? (
                    <p className="roadmap-learn-sidebar__empty">No roles match your search.</p>
                  ) : (
                    <ul className="roadmap-learn-sidebar__rail-list">
                      {visibleRoles.map((role) => {
                        const isSelected = selectedRole?.id === role.id;
                        return (
                          <li key={role.id} className="roadmap-learn-sidebar__rail-item">
                            <button
                              type="button"
                              className={[
                                'roadmap-learn-sidebar__rail-btn',
                                'roadmap-learn-sidebar__rail-btn--role',
                                isSelected ? 'roadmap-learn-sidebar__rail-btn--selected' : ''
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              aria-current={isSelected ? 'true' : undefined}
                              onClick={() => onSelectRole?.(role)}
                              onKeyDown={(e) => handleRoleKeyDown(e, role)}
                            >
                              {role.iconClass ? (
                                <i
                                  className={`ti ${role.iconClass} roadmap-learn-sidebar__role-icon`}
                                  aria-hidden="true"
                                />
                              ) : (
                                <span className="roadmap-learn-sidebar__role-icon-fallback" aria-hidden>
                                  <i className="ti ti-user roadmap-learn-sidebar__role-icon" aria-hidden="true" />
                                </span>
                              )}
                              <span className="roadmap-learn-sidebar__rail-title">{role.title}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )
                ) : (
                  <p className="roadmap-learn-sidebar__empty">Roles are syncing. Check back soon.</p>
                )}
              </section>
            )}
          </div>
        </div>

        {showFooter ? (
          <nav className="roadmap-learn-sidebar__footer" aria-label={footerAriaLabel}>
            <label className="roadmap-learn-sidebar__footer-search">
              <SearchIcon
                className="roadmap-learn-sidebar__footer-search-icon"
                aria-hidden
                focusable="false"
              />
              <input
                type="search"
                className="roadmap-learn-sidebar__footer-search-input"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                aria-label={searchAriaLabel}
                autoComplete="off"
                spellCheck={false}
              />
            </label>

            <div className="roadmap-learn-sidebar__footer-spacer" aria-hidden />

            {pageCount > 1 ? (
              <>
                <div
                  className="roadmap-learn-sidebar__footer-pagination-status"
                  role="status"
                  aria-live="polite"
                  aria-label={`Page ${listPage + 1} of ${pageCount}, showing ${listRangeStart} to ${listRangeEnd} of ${activeItems.length}`}
                >
                  <span className="roadmap-learn-sidebar__footer-pagination-page">
                    {listPage + 1}
                  </span>
                  <span className="roadmap-learn-sidebar__footer-pagination-sep" aria-hidden>
                    /
                  </span>
                  <span className="roadmap-learn-sidebar__footer-pagination-total">
                    {pageCount}
                  </span>
                </div>
                <div
                  className="roadmap-learn-sidebar__footer-pagination-nav"
                  role="navigation"
                  aria-label={isLanguageMode ? 'Language pages' : 'Role pages'}
                >
                  <button
                    type="button"
                    className="roadmap-learn-sidebar__footer-pagination-btn roadmap-learn-sidebar__footer-pagination-btn--prev"
                    onClick={() => setListPage((p) => Math.max(0, p - 1))}
                    disabled={!canListPagePrev}
                    aria-label={isLanguageMode ? 'Previous page of languages' : 'Previous page of roles'}
                  >
                    <NavBackIcon
                      className="roadmap-learn-sidebar__footer-pagination-btn-icon"
                      aria-hidden
                      focusable="false"
                    />
                  </button>
                  <button
                    type="button"
                    className="roadmap-learn-sidebar__footer-pagination-btn roadmap-learn-sidebar__footer-pagination-btn--next"
                    onClick={() => setListPage((p) => Math.min(pageCount - 1, p + 1))}
                    disabled={!canListPageNext}
                    aria-label={isLanguageMode ? 'Next page of languages' : 'Next page of roles'}
                  >
                    <NavForwardIcon
                      className="roadmap-learn-sidebar__footer-pagination-btn-icon"
                      aria-hidden
                      focusable="false"
                    />
                  </button>
                </div>
              </>
            ) : (
              <p
                className="roadmap-learn-sidebar__footer-summary"
                role="status"
                aria-live="polite"
              >
                {hasActiveSearch
                  ? `${activeItems.length} of ${catalogCount} ${isLanguageMode ? 'languages' : 'roles'}`
                  : `${catalogCount} ${isLanguageMode ? (catalogCount === 1 ? 'language' : 'languages') : catalogCount === 1 ? 'role' : 'roles'}`}
              </p>
            )}
          </nav>
        ) : null}
      </div>
    </aside>
  );
}
